<template>
  <div class="user-list-container">

    <!-- ── PAGE HEADER ── -->
    <div class="page-header">
      <div class="breadcrumb">ຈັດການລະບົບ / <span>ຜູ້ໃຊ້ງານ</span></div>
      <div class="header-row">
        <div class="header-left">
          <h1><i class="fas fa-users-cog"></i> ຈັດການຜູ້ໃຊ້ລະບົບ</h1>
          <p>ເພີ່ມ, ແກ້ໄຂ, ຄົ້ນຫາ ແລະ ຈັດການຜູ້ໃຊ້ງານທັງໝົດ</p>
        </div>
        <button class="btn-add" @click="$router.push('/home/system/users/new')">
          <i class="fas fa-user-plus"></i> ເພີ່ມຜູ້ໃຊ້ໃໝ່
        </button>
      </div>
    </div>

    <!-- ── STATS STRIP ── -->
    <div class="stats-strip">
      <div class="stat-card">
        <div class="stat-icon purple"><i class="fas fa-users"></i></div>
        <div>
          <div class="stat-num">{{ totalRows }}</div>
          <div class="stat-lbl">ຜູ້ໃຊ້ທັງໝົດ</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon green"><i class="fas fa-user-check"></i></div>
        <div>
          <div class="stat-num">{{ activeCount }}</div>
          <div class="stat-lbl">ເປີດໃຊ້ງານ</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon orange"><i class="fas fa-user-shield"></i></div>
        <div>
          <div class="stat-num">{{ adminCount }}</div>
          <div class="stat-lbl">Admin</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon red"><i class="fas fa-user-times"></i></div>
        <div>
          <div class="stat-num">{{ totalRows - activeCount }}</div>
          <div class="stat-lbl">ປິດໃຊ້ງານ</div>
        </div>
      </div>
    </div>

    <!-- ── FILTER PANEL ── -->
    <div class="filter-panel">
      <div class="filter-row">
        <div class="search-wrap">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="ຄົ້ນຫາດ້ວຍ ຊື່ຜູ້ໃຊ້ ຫຼື ຊື່ເຕັມ..."
            @keyup.enter="handleSearch"
          />
          <button v-if="searchQuery" class="clear-btn" @click="searchQuery=''; handleSearch()">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <select v-model="filterLevel" @change="handleSearch" class="filter-select">
          <option value="">-- ທຸກລະດັບ --</option>
          <option value="1">Admin</option>
          <option value="2">Manager</option>
          <option value="3">Teacher</option>
        </select>

        <select v-model="filterActive" @change="handleSearch" class="filter-select">
          <option value="">-- ທຸກສະຖານະ --</option>
          <option value="1">ເປີດໃຊ້ງານ</option>
          <option value="0">ປິດໃຊ້ງານ</option>
        </select>

        <button class="btn-search" @click="handleSearch">
          <i class="fas fa-filter"></i> ຄົ້ນຫາ
        </button>
        <button class="btn-reset" @click="resetFilters" title="ລ້າງການຄົ້ນຫາ">
          <i class="fas fa-undo"></i>
        </button>
      </div>
    </div>

    <!-- ── TABLE ── -->
    <div class="panel">
      <div class="panel-body">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th style="width:50px">#</th>
                <th style="width:160px">ຊື່ຜູ້ໃຊ້</th>
                <th>ຊື່ ແລະ ນາມສະກຸນ</th>
                <th style="width:120px">ໂຮງຮຽນ/ສັງກັດ</th>
                <th style="width:110px">ລະດັບສິດ</th>
                <th style="width:100px" class="text-center">ສະຖານະ</th>
                <th style="width:120px" class="text-center">ຈັດການ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="td-center td-loading">
                  <i class="fas fa-spinner fa-spin"></i> ກຳລັງໂຫຼດຂໍ້ມູນ...
                </td>
              </tr>
              <tr v-else-if="users.length === 0">
                <td colspan="7" class="td-center td-empty">
                  <i class="fas fa-user-slash fa-2x"></i>
                  <div>ບໍ່ພົບຂໍ້ມູນຜູ້ໃຊ້</div>
                </td>
              </tr>
              <tr v-else v-for="(user, index) in users" :key="user._id"
                  :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                <td class="td-num">{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                <td>
                  <div class="user-info">
                    <div class="avatar" :style="{ background: avatarColor(user.username) }">
                      {{ initials(user.username) }}
                    </div>
                    <span class="username">{{ user.username }}</span>
                  </div>
                </td>
                <td class="td-name">{{ user.fullname || '—' }}</td>
                <td class="td-loc">{{ user.school_id || '—' }}</td>
                <td>
                  <span :class="['role-badge', levelClass(user.user_level)]">
                    <i :class="levelIcon(user.user_level)"></i>
                    {{ levelLabel(user.user_level) }}
                  </span>
                </td>
                <td class="text-center">
                  <span :class="['status-pill', user.active ? 'active' : 'inactive']">
                    <i :class="user.active ? 'fas fa-circle' : 'far fa-circle'"></i>
                    {{ user.active ? 'ເປີດໃຊ້' : 'ປິດໃຊ້' }}
                  </span>
                </td>
                <td class="text-center">
                  <button class="action-btn edit"   @click="handleEdit(user)"           title="ແກ້ໄຂ"><i class="fas fa-edit"></i></button>
                  <button class="action-btn pwd"    @click="openPwdModal(user)"         title="ປ່ຽນລະຫັດ"><i class="fas fa-key"></i></button>
                  <button class="action-btn delete" @click="openDelModal(user)"         title="ລົບ"><i class="fas fa-trash-alt"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── PAGINATION ── -->
      <div class="pagination-bar">
        <div class="page-info">
          ສະແດງ <b>{{ startItem }}–{{ endItem }}</b> ຈາກ <b>{{ totalRows }}</b> ລາຍການ
        </div>
        <div class="page-controls">
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(1)">
            <i class="fas fa-angle-double-left"></i>
          </button>
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
            <i class="fas fa-angle-left"></i>
          </button>
          <button
            v-for="p in visiblePages" :key="p"
            class="page-btn"
            :class="{ active: p === currentPage, ellipsis: p === '...' }"
            :disabled="p === '...'"
            @click="p !== '...' && changePage(p)"
          >{{ p }}</button>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
            <i class="fas fa-angle-right"></i>
          </button>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(totalPages)">
            <i class="fas fa-angle-double-right"></i>
          </button>
        </div>
        <select v-model.number="pageSize" @change="currentPage=1; fetchUsers()" class="page-size-select">
          <option :value="10">10 / ໜ້າ</option>
          <option :value="20">20 / ໜ້າ</option>
          <option :value="50">50 / ໜ້າ</option>
        </select>
      </div>
    </div>

    <!-- ─────────────────────────────────────────────────────────
         MODAL: Change Password
    ──────────────────────────────────────────────────────────── -->
    <transition name="modal-fade">
      <div v-if="pwdModal.show" class="modal-overlay" @mousedown.self="pwdModal.show = false">
        <div class="modal-card modal-sm">
          <div class="modal-header">
            <div class="modal-title">
              <div class="modal-icon-wrap"><i class="fas fa-key"></i></div>
              <span>ປ່ຽນລະຫັດຜ່ານ</span>
            </div>
            <button class="modal-close" @click="pwdModal.show = false"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-body">
            <div class="pwd-target">
              <div class="avatar lg" :style="{ background: avatarColor(pwdModal.user && pwdModal.user.username) }">
                {{ initials(pwdModal.user && pwdModal.user.username) }}
              </div>
              <div>
                <div class="pwd-username">{{ pwdModal.user && pwdModal.user.username }}</div>
                <div class="pwd-fullname">{{ pwdModal.user && pwdModal.user.fullname }}</div>
              </div>
            </div>
            <div class="form-group mt">
              <label>ລະຫັດຜ່ານໃໝ່ <span class="req">*</span></label>
              <div class="input-pw-wrap">
                <input
                  v-model="pwdModal.newPwd"
                  :type="pwdModal.show_pw ? 'text' : 'password'"
                  placeholder="ຢ່າງໜ້ອຍ 6 ຕົວອັກສອນ"
                  @keyup.enter="doChangePwd"
                />
                <button type="button" class="toggle-pw" @click="pwdModal.show_pw = !pwdModal.show_pw">
                  <i :class="pwdModal.show_pw ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
              <div v-if="pwdModal.newPwd && pwdModal.newPwd.length < 6" class="field-error">
                ລະຫັດຜ່ານຕ້ອງມີຢ່າງໜ້ອຍ 6 ຕົວ
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="pwdModal.show = false">ຍົກເລີກ</button>
            <button class="btn-save" :disabled="pwdModal.loading || !pwdModal.newPwd || pwdModal.newPwd.length < 6" @click="doChangePwd">
              <i class="fas" :class="pwdModal.loading ? 'fa-spinner fa-spin' : 'fa-save'"></i>
              {{ pwdModal.loading ? 'ກຳລັງບັນທຶກ...' : 'ປ່ຽນລະຫັດຜ່ານ' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ─────────────────────────────────────────────────────────
         MODAL: Delete Confirm
    ──────────────────────────────────────────────────────────── -->
    <transition name="modal-fade">
      <div v-if="delModal.show" class="modal-overlay" @mousedown.self="delModal.show = false">
        <div class="modal-card modal-sm">
          <div class="modal-header danger">
            <div class="modal-title">
              <div class="modal-icon-wrap"><i class="fas fa-trash-alt"></i></div>
              <span>ຢືນຢັນການລົບ</span>
            </div>
            <button class="modal-close" @click="delModal.show = false"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-body del-body">
            <p>ທ່ານຕ້ອງການລົບຜູ້ໃຊ້</p>
            <div class="del-target">
              <div class="avatar" :style="{ background: avatarColor(delModal.user && delModal.user.username) }">
                {{ initials(delModal.user && delModal.user.username) }}
              </div>
              <div>
                <strong>{{ delModal.user && delModal.user.username }}</strong>
                <div style="font-size:12px;color:#666">{{ delModal.user && delModal.user.fullname }}</div>
              </div>
            </div>
            <p class="del-warn">ການກະທຳນີ້ <b>ບໍ່ສາມາດຍ້ອນຄືນໄດ້</b></p>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="delModal.show = false">ຍົກເລີກ</button>
            <button class="btn-danger" :disabled="delModal.loading" @click="doDelete">
              <i class="fas" :class="delModal.loading ? 'fa-spinner fa-spin' : 'fa-trash-alt'"></i>
              {{ delModal.loading ? 'ກຳລັງລົບ...' : 'ຢືນຢັນລົບ' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── TOAST ── -->
    <transition name="toast-slide">
      <div v-if="toast.show" :class="['toast', toast.type]">
        <i :class="toast.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
        {{ toast.msg }}
      </div>
    </transition>

  </div>
</template>

<script>
import api from '@/utils/api';

const AVATAR_COLORS = [
  '#4527A0','#1565C0','#00695C','#558B2F',
  '#E65100','#AD1457','#6A1B9A','#0277BD',
];

const LEVEL_MAP = {
  1: { label: 'Admin',   cls: 'admin',   icon: 'fas fa-shield-alt' },
  2: { label: 'Manager', cls: 'manager', icon: 'fas fa-user-tie'   },
  3: { label: 'Teacher', cls: 'teacher', icon: 'fas fa-chalkboard-teacher' },
};

export default {
  name: 'UserListPage',
  data() {
    return {
      users: [],
      currentPage: 1,
      pageSize: 10,
      totalRows: 0,
      searchQuery: '',
      filterLevel: '',
      filterActive: '',
      loading: false,
      pwdModal: { show: false, user: null, newPwd: '', show_pw: false, loading: false },
      delModal: { show: false, user: null, loading: false },
      toast: { show: false, type: 'success', msg: '' },
    };
  },
  computed: {
    totalPages()  { return Math.ceil(this.totalRows / this.pageSize) || 1; },
    startItem()   { return this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; },
    endItem()     { return Math.min(this.currentPage * this.pageSize, this.totalRows); },
    activeCount() { return this.users.filter(u => u.active).length; },
    adminCount()  { return this.users.filter(u => u.user_level === 1).length; },
    visiblePages() {
      const total = this.totalPages;
      const cur   = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [1];
      if (cur > 3)           pages.push('...');
      for (let p = Math.max(2, cur - 1); p <= Math.min(total - 1, cur + 1); p++) pages.push(p);
      if (cur < total - 2)   pages.push('...');
      pages.push(total);
      return pages;
    },
  },
  mounted() { this.fetchUsers(); },
  methods: {
    async fetchUsers() {
      this.loading = true;
      try {
        const res = await api.get('/users', {
          params: {
            page:       this.currentPage,
            limit:      this.pageSize,
            search:     this.searchQuery,
            user_level: this.filterLevel,
            active:     this.filterActive,
          }
        });
        if (res.data.code === 1) {
          this.users     = res.data.rows;
          this.totalRows = res.data.totalRows;
        } else {
          this.users = []; this.totalRows = 0;
        }
      } catch (e) {
        console.error(e);
        this.users = []; this.totalRows = 0;
      } finally {
        this.loading = false;
      }
    },
    handleSearch()    { this.currentPage = 1; this.fetchUsers(); },
    resetFilters()    { this.searchQuery = ''; this.filterLevel = ''; this.filterActive = ''; this.handleSearch(); },
    changePage(p)     { if (p >= 1 && p <= this.totalPages) { this.currentPage = p; this.fetchUsers(); } },
    handleEdit(user)  { this.$router.push(`/home/system/users/edit/${user._id}`); },

    openPwdModal(user) {
      this.pwdModal = { show: true, user, newPwd: '', show_pw: false, loading: false };
      document.body.style.overflow = 'hidden';
    },
    async doChangePwd() {
      if (!this.pwdModal.newPwd || this.pwdModal.newPwd.length < 6) return;
      this.pwdModal.loading = true;
      try {
        const res = await api.post('/users/change-password', {
          id: this.pwdModal.user._id,
          password: this.pwdModal.newPwd,
        });
        if (res.data.code === 1) {
          this.pwdModal.show = false;
          document.body.style.overflow = '';
          this.showToast('success', 'ປ່ຽນລະຫັດຜ່ານສຳເລັດ');
        } else {
          this.showToast('error', res.data.desc || 'ເກີດຂໍ້ຜິດພາດ');
        }
      } catch (e) {
        this.showToast('error', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ກັບເຊີເວີ');
      } finally {
        this.pwdModal.loading = false;
      }
    },

    openDelModal(user) {
      this.delModal = { show: true, user, loading: false };
      document.body.style.overflow = 'hidden';
    },
    async doDelete() {
      this.delModal.loading = true;
      try {
        const res = await api.delete(`/del/users/${this.delModal.user._id}`);
        if (res.data.code === 1) {
          this.delModal.show = false;
          document.body.style.overflow = '';
          this.showToast('success', 'ລົບຜູ້ໃຊ້ສຳເລັດ');
          if (this.users.length === 1 && this.currentPage > 1) this.currentPage--;
          this.fetchUsers();
        } else {
          this.showToast('error', res.data.desc || 'ລົບບໍ່ສຳເລັດ');
        }
      } catch (e) {
        this.showToast('error', 'ເກີດຂໍ້ຜິດພາດໃນການລົບ');
      } finally {
        this.delModal.loading = false;
      }
    },

    showToast(type, msg) {
      this.toast = { show: true, type, msg };
      setTimeout(() => { this.toast.show = false; }, 3500);
    },
    initials(name) {
      if (!name) return '?';
      return name.slice(0, 2).toUpperCase();
    },
    avatarColor(name) {
      if (!name) return AVATAR_COLORS[0];
      const i = name.charCodeAt(0) % AVATAR_COLORS.length;
      return AVATAR_COLORS[i];
    },
    levelLabel(lvl) { return (LEVEL_MAP[lvl] || { label: 'User' }).label; },
    levelClass(lvl) { return (LEVEL_MAP[lvl] || { cls: 'user' }).cls; },
    levelIcon(lvl)  { return (LEVEL_MAP[lvl] || { icon: 'fas fa-user' }).icon; },
  },
};
</script>

<style scoped>
/* ─────── Base ─────── */
.user-list-container { animation: fadeIn 0.35s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

/* ─────── Header ─────── */
.header-row  { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 8px; flex-wrap: wrap; gap: 12px; }
.header-left h1 { font-size: 1.5rem; color: #311B92; margin: 0 0 4px; }
.header-left p  { color: var(--text3); font-size: 13px; margin: 0; }

.btn-add {
  display: flex; align-items: center; gap: 8px;
  background: linear-gradient(135deg, #4527A0, #311B92);
  color: #fff; border: none; padding: 10px 20px;
  border-radius: 10px; font-size: 14px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
  box-shadow: 0 4px 14px rgba(69,39,160,.35); transition: all .2s;
}
.btn-add:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(69,39,160,.45); }

/* ─────── Stats ─────── */
.stats-strip { display: flex; gap: 16px; margin: 20px 0; flex-wrap: wrap; }
.stat-card   { display: flex; align-items: center; gap: 14px; background: #fff; padding: 16px 22px;
               border-radius: 12px; box-shadow: 0 2px 10px rgba(0,0,0,.06); flex: 1; min-width: 140px; }
.stat-icon   { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center;
               justify-content: center; font-size: 18px; color: #fff; flex-shrink: 0; }
.stat-icon.purple { background: linear-gradient(135deg, #4527A0, #5E35B1); }
.stat-icon.green  { background: linear-gradient(135deg, #1B5E20, #2E7D32); }
.stat-icon.orange { background: linear-gradient(135deg, #E65100, #F4511E); }
.stat-icon.red    { background: linear-gradient(135deg, #B71C1C, #C62828); }
.stat-num { font-size: 1.6rem; font-weight: 700; color: #1e293b; line-height: 1; }
.stat-lbl { font-size: 12px; color: var(--text3); margin-top: 3px; }

/* ─────── Filter ─────── */
.filter-panel { background: #fff; border-radius: 12px; padding: 14px 18px;
                box-shadow: 0 2px 10px rgba(0,0,0,.06); margin-bottom: 16px; }
.filter-row   { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.search-wrap  { position: relative; flex: 1; min-width: 220px; }
.search-wrap input {
  width: 100%; padding: 10px 36px; border: 1px solid var(--border);
  border-radius: 8px; font-size: 13px; transition: border .2s; box-sizing: border-box;
}
.search-wrap input:focus { border-color: #4527A0; outline: none; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text3); font-size: 13px; }
.clear-btn   { position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
               background: none; border: none; color: var(--text3); cursor: pointer; padding: 0; }
.filter-select { padding: 10px 12px; border: 1px solid var(--border); border-radius: 8px;
                 font-size: 13px; min-width: 130px; cursor: pointer; }
.filter-select:focus { border-color: #4527A0; outline: none; }
.btn-search {
  display: flex; align-items: center; gap: 6px;
  background: #4527A0; color: #fff; border: none;
  padding: 10px 18px; border-radius: 8px; font-size: 13px;
  font-weight: 600; cursor: pointer; white-space: nowrap;
}
.btn-search:hover { background: #311B92; }
.btn-reset { background: #f1f5f9; border: 1px solid var(--border); color: var(--text2);
             padding: 10px 14px; border-radius: 8px; cursor: pointer; }
.btn-reset:hover { background: #e2e8f0; }

/* ─────── Table ─────── */
.panel-body { padding: 0; }
.table-wrap { overflow-x: auto; border-radius: 0 0 12px 12px; }
table { width: 100%; border-collapse: collapse; min-width: 680px; }
th { background: linear-gradient(135deg, #4527A0, #5E35B1); color: #fff;
     padding: 12px 14px; text-align: left; font-size: 13px; font-weight: 600; white-space: nowrap; }
td { padding: 11px 14px; font-size: 13px; color: var(--text1); border-bottom: 1px solid #f1f5f9; }
.row-even { background: #fff; }
.row-odd  { background: #fafbff; }
tr:hover td { background: #f0ebff !important; }
.td-center  { text-align: center; }
.td-loading, .td-empty { color: var(--text3); padding: 48px 0 !important; text-align: center; }
.td-empty i { display: block; margin-bottom: 8px; color: #c5b8ff; }
.td-num { color: var(--text3); font-size: 12px; }
.td-name { font-weight: 500; }
.td-loc  { font-size: 12px; color: var(--text3); }
td.text-center { text-align: center; }

/* ─────── User cell ─────── */
.user-info { display: flex; align-items: center; gap: 9px; }
.avatar {
  width: 32px; height: 32px; border-radius: 8px; color: #fff;
  font-size: 12px; font-weight: 700; display: flex; align-items: center;
  justify-content: center; flex-shrink: 0; letter-spacing: .5px;
}
.avatar.lg { width: 44px; height: 44px; font-size: 16px; border-radius: 10px; }
.username { font-weight: 600; color: #311B92; font-size: 13px; }

/* ─────── Badges ─────── */
.role-badge { display: inline-flex; align-items: center; gap: 5px;
              padding: 4px 10px; border-radius: 10px; font-size: 11px; font-weight: 700; }
.role-badge i { font-size: 10px; }
.role-badge.admin   { background: #FFF3E0; color: #E65100; }
.role-badge.manager { background: #E8EAF6; color: #283593; }
.role-badge.teacher { background: #E8F5E9; color: #1B5E20; }
.role-badge.user    { background: #EDE7F6; color: #4527A0; }

.status-pill { display: inline-flex; align-items: center; gap: 5px;
               padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; }
.status-pill i { font-size: 8px; }
.status-pill.active   { background: #E8F5E9; color: #1B5E20; }
.status-pill.inactive { background: #F5F5F5; color: #9E9E9E; }

/* ─────── Action buttons ─────── */
.action-btn { width: 30px; height: 30px; border: none; border-radius: 7px;
              cursor: pointer; font-size: 12px; transition: all .18s; margin: 0 2px; }
.action-btn.edit   { background: #EDE7F6; color: #4527A0; }
.action-btn.pwd    { background: #FFF8E1; color: #F57F17; }
.action-btn.delete { background: #FFEBEE; color: #C62828; }
.action-btn.edit:hover   { background: #4527A0; color: #fff; }
.action-btn.pwd:hover    { background: #F57F17; color: #fff; }
.action-btn.delete:hover { background: #C62828; color: #fff; }

/* ─────── Pagination ─────── */
.pagination-bar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 20px; border-top: 1px solid #f1f5f9; flex-wrap: wrap; gap: 10px;
}
.page-info { font-size: 13px; color: var(--text3); }
.page-controls { display: flex; gap: 4px; flex-wrap: wrap; }
.page-btn {
  min-width: 32px; height: 32px; padding: 0 8px; border: 1px solid var(--border);
  background: #fff; border-radius: 6px; cursor: pointer; font-size: 13px; transition: all .18s;
}
.page-btn:hover:not(:disabled) { background: #EDE7F6; border-color: #4527A0; color: #4527A0; }
.page-btn.active   { background: #4527A0; color: #fff; border-color: #4527A0; font-weight: 700; }
.page-btn:disabled { opacity: .45; cursor: not-allowed; }
.page-btn.ellipsis { cursor: default; background: none; border: none; }
.page-size-select  { padding: 6px 10px; border: 1px solid var(--border); border-radius: 6px; font-size: 12px; }

/* ─────── Modal ─────── */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(17,24,39,.5);
  backdrop-filter: blur(4px); display: flex; align-items: center;
  justify-content: center; z-index: 1000; padding: 16px;
}
.modal-card {
  background: #fff; border-radius: 16px; width: 100%;
  max-width: 480px; box-shadow: 0 25px 50px rgba(0,0,0,.18);
  display: flex; flex-direction: column;
}
.modal-card.modal-sm { max-width: 400px; }

.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 18px 22px; border-radius: 16px 16px 0 0;
  background: linear-gradient(135deg, #4527A0, #311B92);
}
.modal-header.danger { background: linear-gradient(135deg, #B71C1C, #C62828); }
.modal-title { display: flex; align-items: center; gap: 10px; color: #fff; font-size: 15px; font-weight: 700; }
.modal-icon-wrap { width: 32px; height: 32px; background: rgba(255,255,255,.2);
                   border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.modal-close { background: rgba(255,255,255,.15); border: none; border-radius: 8px;
               color: #fff; width: 28px; height: 28px; cursor: pointer; font-size: 13px; }
.modal-close:hover { background: rgba(255,255,255,.3); }

.modal-body { padding: 22px; }
.modal-footer {
  padding: 14px 22px; border-top: 1px solid #f1f5f9;
  display: flex; justify-content: flex-end; gap: 10px;
  border-radius: 0 0 16px 16px;
}

/* Password modal */
.pwd-target { display: flex; align-items: center; gap: 12px; margin-bottom: 18px;
              padding: 14px; background: #F3E5F5; border-radius: 10px; }
.pwd-username { font-weight: 700; color: #311B92; font-size: 15px; }
.pwd-fullname { font-size: 12px; color: var(--text3); margin-top: 2px; }
.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-group label { font-size: 13px; font-weight: 600; color: var(--text2); }
.form-group.mt { margin-top: 4px; }
.input-pw-wrap { position: relative; }
.input-pw-wrap input {
  width: 100%; padding: 10px 40px 10px 12px; border: 1px solid var(--border);
  border-radius: 8px; font-size: 13px; transition: border .2s; box-sizing: border-box;
}
.input-pw-wrap input:focus { border-color: #4527A0; outline: none; box-shadow: 0 0 0 3px rgba(69,39,160,.12); }
.toggle-pw { position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
             background: none; border: none; color: var(--text3); cursor: pointer; font-size: 14px; }
.field-error { font-size: 11px; color: #C62828; margin-top: 2px; }
.req { color: #e53e3e; }

/* Delete modal */
.del-body { text-align: center; }
.del-body p { color: var(--text2); font-size: 14px; margin: 6px 0; }
.del-target { display: flex; align-items: center; justify-content: center; gap: 12px;
              background: #FFEBEE; border-radius: 10px; padding: 14px 20px; margin: 12px 0; color: #B71C1C; }
.del-warn { font-size: 12px; color: var(--text3); }

/* Buttons */
.btn-cancel { padding: 9px 18px; border: 1px solid var(--border); background: #fff;
              border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; color: var(--text2); }
.btn-cancel:hover { background: #f8f9fa; }
.btn-save {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 22px; background: linear-gradient(135deg, #4527A0, #311B92);
  color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.btn-save:disabled { opacity: .65; cursor: not-allowed; }
.btn-danger {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 22px; background: linear-gradient(135deg, #B71C1C, #C62828);
  color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.btn-danger:disabled { opacity: .65; cursor: not-allowed; }

/* ─────── Toast ─────── */
.toast {
  position: fixed; bottom: 28px; right: 28px; z-index: 2000;
  display: flex; align-items: center; gap: 10px;
  padding: 14px 20px; border-radius: 12px; font-size: 14px; font-weight: 600;
  color: #fff; box-shadow: 0 8px 24px rgba(0,0,0,.18); min-width: 240px;
}
.toast.success { background: linear-gradient(135deg, #1B5E20, #2E7D32); }
.toast.error   { background: linear-gradient(135deg, #B71C1C, #C62828); }

/* ─────── Transitions ─────── */
.modal-fade-enter-active, .modal-fade-leave-active { transition: all .25s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter .modal-card, .modal-fade-leave-to .modal-card { transform: translateY(-12px) scale(.97); }
.toast-slide-enter-active, .toast-slide-leave-active { transition: all .3s ease; }
.toast-slide-enter { opacity: 0; transform: translateX(40px); }
.toast-slide-leave-to { opacity: 0; transform: translateX(40px); }

/* ─────── Responsive ─────── */
@media (max-width: 640px) {
  .header-row, .filter-row { flex-direction: column; }
  .stats-strip { gap: 10px; }
  .stat-card { min-width: unset; }
}
</style>
